import { useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { FileData } from "lib/util";
import { OBJECT_ID_ASRS_LOCK, OBJECT_ID_CREATE_CONTAINMENT, OBJECT_ID_HOLD_CONTAINMENT } from "pages/Users/constants";
import { ContainmentTypeConfig } from "./containmentTypeConfig";
import * as constants from "lib/util";

// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
interface PermissionApi {
	getPlantIdsByObjectAndSector: (objectId: string, sectorName: string) => string[];
}

// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
interface SectorLike {
	id: string;
	name: string;
}

// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
interface PlantLike {
	id: string;
}

/** Extracts the normalized hold option name from the optional subsequent hold selection. */
function getSelectedHoldName(subsequentHold: Opt<{ name?: string }>): string {
	return subsequentHold
		?.Map((h) => h.name?.toLowerCase())
		.UnwrapOr("");
}

/**
 * Hook that manages permission-based UI element availability for multi-sector containments.
 *
 * Provides functions to determine which sectors, plants, and hold options should be
 * disabled based on user permissions across multiple sectors. Key behaviors:
 *
 * - Enforces RESS exclusivity: RESS sector cannot be mixed with GPS sectors
 * - Validates CREATE_CONTAINMENT permission per sector before allowing selection
 * - Validates HOLD_CONTAINMENT permission when "hold" subsequent option is selected
 * - Automatically removes invalid selections when hold option changes
 * - Ensures plants are only selectable if user has required permissions in at least one selected sector
 */
// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
export function useContainmentSectorPermissions({
	form,
	permissions,
	watchedSectors,
	watchedSubsequentHold,
	containmentTypeName,
	typeConfig,
}: {
	form: UseFormReturn<FileData>;
	permissions: PermissionApi;
	watchedSectors: SectorLike[];
	watchedSubsequentHold: Opt<{ name?: string }>;
	containmentTypeName?: string;
	typeConfig?: ContainmentTypeConfig;
}) {
	const getHoldOptionDisabled = useMemo(() => {
		return (option: { name?: string }) => {
			const optionName = option.name?.toLowerCase();
			if (!watchedSectors || watchedSectors.length === 0) {
				return false;
			}

			if (optionName === "hold") {
				for (const sector of watchedSectors) {
					const holdPlantIds = permissions.getPlantIdsByObjectAndSector(
						OBJECT_ID_HOLD_CONTAINMENT,
						sector.name
					);
					if (holdPlantIds.length === 0) {
						return true;
					}
				}
			}

			return false;
		};
	}, [permissions, watchedSectors]);

	const createObjectId = typeConfig?.usesLockPermissions
		? OBJECT_ID_ASRS_LOCK
		: OBJECT_ID_CREATE_CONTAINMENT;

	const getImpactedPlantOptionDisabled = useMemo(() => {
		return (impactedPlant: PlantLike) => {
			if (!watchedSectors || watchedSectors.length === 0) {
				return true;
			}

			let hasCreateInAnySector = false;
			let hasHoldInAnySector = false;

			for (const sector of watchedSectors) {
				const plantIds = permissions.getPlantIdsByObjectAndSector(
					createObjectId,
					sector.name
				);

				if (plantIds.includes(impactedPlant.id)) {
					hasCreateInAnySector = true;

					if (getSelectedHoldName(watchedSubsequentHold) === "hold") {
						const holdPlantIds = permissions.getPlantIdsByObjectAndSector(
							OBJECT_ID_HOLD_CONTAINMENT,
							sector.name
						);
						if (holdPlantIds.includes(impactedPlant.id)) {
							hasHoldInAnySector = true;
						}
					} else {
						hasHoldInAnySector = true;
					}
				}
			}

			if (!hasCreateInAnySector) {
				return true;
			}

			return (
				getSelectedHoldName(watchedSubsequentHold) === "hold" &&
				!hasHoldInAnySector
			);
		};
	}, [createObjectId, permissions, watchedSectors, watchedSubsequentHold]);

	const getSectorOptionDisabled = useMemo(() => {
		return (sector: SectorLike) => {
			if (containmentTypeName) {
				const sectorTypes = (sector as any).sectorTypes;
				if (Array.isArray(sectorTypes)) {
					const hasMatchingType = sectorTypes.some(
						(st: any) => st.type?.name === containmentTypeName
					);
					if (!hasMatchingType) return true;
				} else if (sectorTypes?.Find) {
					const match = sectorTypes.Find(
						(st: any) => st.type?.name === containmentTypeName
					);
					if (!match?.IsSome()) return true;
				}
			}

			if (watchedSectors.some((s) => s.name === constants.RESS)) {
				return sector.name !== constants.RESS;
			}

			if (watchedSectors.length > 0 && sector.name === constants.RESS) {
				return true;
			}

			const permittedPlantIds = permissions.getPlantIdsByObjectAndSector(
				createObjectId,
				sector.name
			);
			if (permittedPlantIds.length === 0) {
				return true;
			}

			if (getSelectedHoldName(watchedSubsequentHold) === "hold") {
				const holdPlantIds = permissions.getPlantIdsByObjectAndSector(
					OBJECT_ID_HOLD_CONTAINMENT,
					sector.name
				);
				return holdPlantIds.length === 0;
			}

			return false;
		};
	}, [containmentTypeName, createObjectId, permissions, watchedSectors, watchedSubsequentHold]);

	const handleSubsequentHoldChange = useCallback(
		({ value }: { value: { name?: string } | null }) => {
			if (!value || value.name?.toLowerCase() !== "hold") {
				return;
			}

			const selectedSectors = form.getValues("sectors");
			if (!selectedSectors || selectedSectors.length === 0) {
				return;
			}

			const validSectors = selectedSectors.filter((sector) => {
				const holdPlantIds = permissions.getPlantIdsByObjectAndSector(
					OBJECT_ID_HOLD_CONTAINMENT,
					sector.name
				);
				return holdPlantIds.length > 0;
			});

			if (validSectors.length !== selectedSectors.length) {
				form.setValue("sectors", validSectors);
			}

			const selectedImpactedPlants = form.getValues("plants");
			if (!Array.isArray(selectedImpactedPlants) || selectedImpactedPlants.length === 0) {
				return;
			}

			const validImpactedPlants = selectedImpactedPlants.filter((impactedPlant: any) =>
				validSectors.some((sector) => {
					const holdPlantIds = permissions.getPlantIdsByObjectAndSector(
						OBJECT_ID_HOLD_CONTAINMENT,
						sector.name
					);
					return holdPlantIds.includes(impactedPlant.id);
				})
			);

			form.setValue("plants", validImpactedPlants);
			if (validImpactedPlants.length === 0) {
				form.clearErrors("plants");
			}
		},
		[form, permissions]
	);

	return {
		getHoldOptionDisabled,
		getImpactedPlantOptionDisabled,
		getSectorOptionDisabled,
		handleSubsequentHoldChange,
	};
}
